'use client'
import React, { useState, ReactElement, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import LoginForm from '@/components/forms/LoginForm'
import ResetPasswordForm from '@/components/forms/ResetPasswordForm'
import SelectAddressForm from '@/components/forms/selectAddressForm'
import { setForCustomerId } from '@/lib/sessionStorage'
import { Geolocation } from '@/lib/interfaces'
import { MessageProps } from '@/components/controls/Message'
import logger from '@/lib/utils/logger'

const LoginPageContent = ({redirectTo, forCustomerId, macAddress}: {redirectTo?: string, forCustomerId?: string, macAddress?: string}): ReactElement => {
  const [showResetForm, setShowResetForm] = useState<boolean>(false)
  const [loginInProgress, setLoginInProgress] = useState<boolean>(false)
  const [addresses, setAddresses] = useState<Geolocation[]>([])
  const [selectedAddress, setSelectedAddress] = useState<Geolocation | null>(null)
  const [showAddressSelection, setShowAddressSelection] = useState<boolean>(false)
  const [resetMessage, setResetMessage] = useState<MessageProps | undefined>(undefined)

  const router = useRouter()
  
  if (!redirectTo) {
    redirectTo = '/'
  }

  const handleAddressSelect = async (address: Geolocation) => {
    setSelectedAddress(address)
    setShowAddressSelection(false)
    setLoginInProgress(true)

    // Retry MAC login with selected address
    const response = await fetch('/api/auth/macLogin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ macAddress, geolocationId: address.id }),
    })
    const result = await response.json()
    if (result.success === true) {
      if (result.forCustomerId) {
        setForCustomerId(result.forCustomerId)
        logger.log(`Saved forCustomerId to session storage: ${result.forCustomerId}`)
      }
      router.push(new URL(redirectTo, window.location.origin).toString())
      logger.log(`Successfully logged in with MAC address: ${macAddress} and address: ${address.street}`)
    } else {
      logger.log(`MAC login failed even with selected address:`, result)
      setLoginInProgress(false)
      // Could show an error message here
    }
  }

  const handleAddressCancel = () => {
    setShowAddressSelection(false)
    setAddresses([])
  }

  useEffect(() => {

    const empLogin = async () => {
      setLoginInProgress(true)
      const response = await fetch('/api/auth/employeeLogin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forCustomerId }),
        credentials: 'include',
      })
      const result = await response.json()
      logger.log(`Employee login result: ${JSON.stringify(result)}`)
      if (result.success == true) {
        // Save forCustomerId to session storage for later use
        setForCustomerId(result.user.customerId)
        logger.log(`Saved forCustomerId to session storage: ${result.user.customerId}`)
        if (result.user.active == true) {
          router.push(new URL(redirectTo, window.location.origin).toString())
          logger.log(`Successfully logged in as employee for customer`)
        } else {
          router.push('/signup')
        }
      } else {
        setLoginInProgress(false)
      }
    }

    const macLogin = async () => {
      setLoginInProgress(true)
      const response = await fetch('/api/auth/macLogin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ macAddress, geolocationId: selectedAddress ? selectedAddress.id : undefined }),
      })
      const result = await response.json()
      if (result.success == true) {
        // Save forCustomerId to session storage for later use
        setForCustomerId(result.user.customerId)
        logger.log(`Saved forCustomerId to session storage: ${result.user.customerId}`)
        if (result.user.active == true) {
          router.push(new URL(redirectTo, window.location.origin).toString())
          logger.log(`Successfully logged in with MAC address: ${macAddress}`)
        } else {
          router.push('/signup')
        }
      } else {
        logger.log(`MAC login failed:`, result)
        if (result.addresses && result.addresses.length > 0) {
          setAddresses(result.addresses)
          setShowAddressSelection(true)
        }
        setLoginInProgress(false)
      }
    }

    if (forCustomerId) {
      logger.log(`Customer ID from search params: ${forCustomerId}`)
      empLogin()
    } else if (macAddress) {
      logger.log(`MAC address from search params: ${macAddress}`)
      macLogin()
    } else {
      logger.log('No customer ID or MAC address provided in search params.')
    }
  }, [forCustomerId, macAddress])


  const showReset = (message?: MessageProps) => {
    setShowResetForm(true)
    if (message) {
      setResetMessage(message)
    }
  }

  const showLogin = () => {
    setShowResetForm(false)
  }

  return (
    <div className="flex flex-col items-center justify-center p-6 min-h-screen bg-login-gradient">
      <div className="text-center mb-8">
        <div className="inline-block p-4 w-full max-w-md">
          <Image
            src="/logo_dark.png"
            alt="Alyrica Logo"
            width={640}
            height={110}
            style={{ height: 'auto', width: '100%' }}
          />
        </div>
        <h1 className="text-4xl font-bold text-white mt-4">Customer Portal</h1>
      </div>
      { loginInProgress ? (
        <div className="text-white text-lg my-20">Logging in...</div>
      ) : showAddressSelection ? (
        <SelectAddressForm 
          addresses={addresses}
          onAddressSelect={handleAddressSelect}
          onCancel={handleAddressCancel}
        />
      ) : showResetForm ? (
        <ResetPasswordForm showLogin={showLogin} initialMessage={resetMessage} />
      ) : (
        <LoginForm showReset={showReset} redirectTo={redirectTo}/>
      )}
      <div className="mt-8 text-center text-white">
        <p className="text-md">Need help? Call us at <a href="tel:5419293330">(541) 929-3330</a></p>
      </div>
    </div>
  )
}

export default LoginPageContent